import type { Territory } from "$global-providers/config-provider/provider";
import { useConfig } from "$global-providers/config-provider/provider";
import * as Sentry from "@sentry/react";
import React, { useRef } from "react";
import * as RR from "react-router-dom";
import { isValidBrowserVersion } from "./browser-support";

function initialiseSentry({
  territory,
  sampleRate,
  tracesSampleRate,
  replaysSessionSampleRate,
  replaysOnErrorSampleRate,
}: {
  territory: Territory;
  sampleRate: number;
  tracesSampleRate: {
    default: number;
  };
  replaysSessionSampleRate: number;
  replaysOnErrorSampleRate: number;
}) {
  Sentry.init({
    dsn: "https://4f4a6ea6ac2779d3b8d36e9bd10c80f0@o344592.ingest.sentry.io/4506081710374912",
    integrations: [
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.reactRouterV6Instrumentation(
          React.useEffect,
          RR.useLocation,
          RR.useNavigationType,
          RR.createRoutesFromChildren,
          RR.matchRoutes,
        ),
      }),
      new Sentry.Replay(),
    ],
    sampleRate,
    tracesSampleRate: tracesSampleRate.default,
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    release: SENTRY_RELEASE,
    transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
  });
  Sentry.setUser({ ip_address: "{{auto}}" });
  Sentry.setTag("territory", territory);
}

export const useInitialiseSentry = () => {
  const { environment, territory, sentry } = useConfig();
  // Using a ref so the effect only runs once and doesn't cause it's caller to re-render
  const hasInitialised = useRef(false);

  React.useEffect(() => {
    if (
      environment !== "production" ||
      hasInitialised.current ||
      !isValidBrowserVersion()
    ) {
      return;
    }
    initialiseSentry({
      territory,
      sampleRate: sentry.sampleRate,
      tracesSampleRate: sentry.tracesSampleRate,
      replaysSessionSampleRate: sentry.replaysSessionSampleRate,
      replaysOnErrorSampleRate: sentry.replaysOnErrorSampleRate,
    });
    hasInitialised.current = true;
  }, [environment, territory, sentry]);
};
